import qs from "qs"
import { waitFor } from "xstate"
import { LinkIcon } from "lucide-react"
import { Link, Outlet, useLoaderData } from "react-router"

import { FiltersWrapper, type StoredSession } from "@/components/Filters"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"

import { handleResponse, fetchAPI } from "@/utils"

import { authMachine } from "@/machines/Authmachine"

const sessionKey = "applications-filter"

async function loader() {
  await waitFor(authMachine, (state) => state.hasTag("Settled"))

  const snapshot = authMachine.getSnapshot()
  if (!snapshot.hasTag("AuthComplete")) return null as never

  const filters = qs.parse(sessionStorage.getItem(sessionKey) ?? "") as unknown as Partial<StoredSession>

  return await fetchAPI<Application[]>(
    `/api/application${qs.stringify(filters.parsed, { addQueryPrefix: true, encode: false })}`,
  ).then(handleResponse)
}

export function Applications() {
  return (
    <div className="flex size-full flex-col gap-y-3 overflow-x-auto px-20 py-5">
      <FiltersWrapper sessionKey={sessionKey}>
        <FiltersWrapper.Input maxLength={20} name="searchCriteria" label="Name" />
        <FiltersWrapper.Checkbox name="includeInactive" label="Include Inactive" />
      </FiltersWrapper>
      <Outlet />
    </div>
  )
}

function Main() {
  const data = useLoaderData<typeof loader>()

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead></TableHead>
          <TableHead>Name</TableHead>
          <TableHead>
            <span className="sr-only">Active</span>
          </TableHead>
          <TableHead>
            <span className="sr-only">Action</span>
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {data.map((item, index) => (
          <TableRow key={item.name}>
            <TableCell>{index + 1}.</TableCell>
            <TableCell>{item.name}</TableCell>
            <TableCell>
              <Badge>{item.active ? "Active" : "Inactive"}</Badge>
            </TableCell>
            <TableCell>
              <Button asChild variant="link">
                <Link to={`/applications/${item.name}`}>
                  <LinkIcon />
                  View sections
                </Link>
              </Button>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}

Applications.loader = loader
Applications.Main = Main

interface Application {
  name: string
  active: boolean
}
